import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, Card, KpiCard, Spinner,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Factory,
  PackageSearch, RefreshCw, Timer,
} from 'lucide-react';

/**
 * Production-floor Gantt chart.
 *
 * Each work centre gets one row; each scheduled production-order
 * operation is rendered as a positioned bar across the visible day
 * window. The window slides forward / back with the arrow buttons.
 *
 * Live data sources:
 *   /manufacturing/WorkCenter
 *   /manufacturing/ProductionOrder
 *   /manufacturing/ProductionOrderOperation
 *
 * The component is forgiving — operations without a workCenterId or
 * with missing dates are pushed to the "Unassigned" lane instead of
 * being silently dropped.
 */

type OpStatus = 'Planned' | 'InProgress' | 'Completed' | 'Held' | 'Cancelled';

interface WorkCenter extends Record<string, unknown> {
  workCenterId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  /** Live column is capacityPerDay (there is no per-hour capacity). */
  capacityPerDay?: number;
}

interface ProductionOrder extends Record<string, unknown> {
  productionOrderId?: string;
  id?: number | string;
  code?: string;
  productName?: string;
  productId?: number | string;
  quantity?: number;
  qty?: number;
  /** Real schedule window lives on the ORDER, not the operation. */
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  productionStatus?: string;
  status?: string;
}

interface ProductionOp extends Record<string, unknown> {
  productionOrderOperationId?: string;
  id?: number | string;
  productionOrderId?: string;
  workCenterId?: string;
  operationName?: string;
  startDate?: string;
  endDate?: string;
  scheduledStart?: string;
  scheduledEnd?: string;
  qty?: number;
  status?: string;
}

interface Lane {
  id: string;
  name: string;
  capacityPerDay?: number;
  ops: ResolvedOp[];
}

interface ResolvedOp {
  id: string;
  start: Date;
  end: Date;
  label: string;
  status: OpStatus;
  qty: number;
  productName: string;
  orderCode: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const VISIBLE_DAYS = 14;
const ROW_HEIGHT = 40;

export default function ProductionGantt() {
  const [centres, setCentres] = useState<WorkCenter[]>([]);
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [ops, setOps] = useState<ProductionOp[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [windowStart, setWindowStart] = useState<Date>(() => startOfDay(new Date()));
  const [selected, setSelected] = useState<ResolvedOp | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [w, p, o] = await Promise.all([
        apiClient.get('/manufacturing/WorkCenter').then(pickList<WorkCenter>).catch(() => []),
        apiClient.get('/manufacturing/ProductionOrder').then(pickList<ProductionOrder>).catch(() => []),
        apiClient
          .get('/manufacturing/ProductionOrderOperation')
          .then(pickList<ProductionOp>)
          .catch(() => []),
      ]);
      setCentres(w);
      setOrders(p);
      setOps(o);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load production schedule');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  // Once orders load, jump the window to the earliest scheduled order so
  // existing data is visible without hunting with the Prev/Next buttons (e.g.
  // a back-dated order would otherwise be off-screen behind "today"). Runs once.
  const positioned = useRef(false);
  useEffect(() => {
    if (positioned.current || orders.length === 0) return;
    let earliest: Date | null = null;
    for (const o of orders) {
      const d = parseDate(o.plannedStartDate ?? o.actualStartDate);
      if (d && (!earliest || d < earliest)) earliest = d;
    }
    if (earliest && earliest < startOfDay(new Date())) {
      setWindowStart(startOfDay(addDays(earliest, -1)));
    }
    positioned.current = true;
  }, [orders]);

  // The work centre a production order runs on, taken from its first operation
  // that names one (operations carry workCenterId; the order itself does not).
  const orderWorkCenter = useMemo(() => {
    const m = new Map<string, string>();
    for (const op of ops) {
      const oid = String(op.productionOrderId ?? '');
      const wc = String(op.workCenterId ?? '');
      if (oid && wc && wc !== '0' && wc !== 'null' && !m.has(oid)) m.set(oid, wc);
    }
    return m;
  }, [ops]);

  // Build one bar per PRODUCTION ORDER (the schedule window lives on the
  // order — plannedStart/End — not on operations, which carry no dates). Lane
  // = the order's work centre, falling back to an Unassigned lane.
  const lanes = useMemo<Lane[]>(() => {
    const byLane = new Map<string, ResolvedOp[]>();
    const lanesByName = new Map<string, { name: string; capacity?: number }>();

    for (const c of centres) {
      const id = String(c.workCenterId ?? c.id ?? '');
      if (!id) continue;
      lanesByName.set(id, { name: String(c.name ?? c.code ?? id), capacity: Number(c.capacityPerDay ?? 0) || undefined });
      byLane.set(id, []);
    }
    // Catch-all lane for orders without a recognised work centre.
    byLane.set('__unassigned', []);
    lanesByName.set('__unassigned', { name: 'Unassigned' });

    for (const order of orders) {
      const oid = String(order.productionOrderId ?? order.id ?? '');
      const start = parseDate(order.plannedStartDate ?? order.actualStartDate);
      let end = parseDate(order.plannedEndDate ?? order.actualEndDate);
      if (!start) continue;            // no schedule window → can't place a bar
      if (!end || end < start) end = start; // zero-width windows still render as a sliver
      const wc = orderWorkCenter.get(oid) ?? '';
      const laneId = byLane.has(wc) ? wc : '__unassigned';
      const status = normaliseStatus(String(order.productionStatus ?? order.status ?? 'Planned'));
      byLane.get(laneId)!.push({
        id: oid || `o${Math.random()}`,
        start,
        end,
        label: String(order.code ?? order.productName ?? `PO ${oid}`),
        status,
        qty: Number(order.quantity ?? order.qty ?? 0),
        productName: String(order.productName ?? (order.productId != null ? `Product #${order.productId}` : '—')),
        orderCode: String(order.code ?? oid),
      });
    }

    const out: Lane[] = [];
    for (const [id, l] of lanesByName) {
      const laneOps = byLane.get(id) ?? [];
      if (laneOps.length === 0 && id === '__unassigned') continue;
      out.push({ id, name: l.name, capacityPerDay: l.capacity, ops: laneOps });
    }
    // Lanes with the most scheduled work first.
    out.sort((a, b) => b.ops.length - a.ops.length);
    return out;
  }, [centres, orders, orderWorkCenter]);

  // KPIs
  const kpis = useMemo(() => {
    const visibleEnd = addDays(windowStart, VISIBLE_DAYS);
    let inProgress = 0;
    let plannedThisWindow = 0;
    let overdue = 0;
    let completedThisWeek = 0;
    const sevenDaysAgo = addDays(windowStart, -7);
    for (const lane of lanes) {
      for (const op of lane.ops) {
        if (op.status === 'InProgress') inProgress++;
        if (op.status === 'Planned' && op.start < visibleEnd && op.end >= windowStart) plannedThisWindow++;
        if (op.status !== 'Completed' && op.status !== 'Cancelled' && op.end < new Date()) overdue++;
        if (op.status === 'Completed' && op.end >= sevenDaysAgo) completedThisWeek++;
      }
    }
    const barCount = lanes.reduce((s, l) => s + l.ops.length, 0);
    return { inProgress, plannedThisWindow, overdue, completedThisWeek, lanes: lanes.length, ops: barCount };
  }, [lanes, windowStart]);

  const days = useMemo(() => {
    const arr: Date[] = [];
    for (let i = 0; i < VISIBLE_DAYS; i++) arr.push(addDays(windowStart, i));
    return arr;
  }, [windowStart]);

  const today = startOfDay(new Date());
  const todayOffset = Math.round((today.getTime() - windowStart.getTime()) / DAY_MS);

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Production schedule</h1>
          <p className="text-xs text-content-muted">
            {lanes.length} work centre(s) · {orders.length} production order(s) · window {fmtDate(windowStart)} – {fmtDate(addDays(windowStart, VISIBLE_DAYS - 1))}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setWindowStart((d) => addDays(d, -7))}>
            <ArrowLeft className="h-3.5 w-3.5" /> Prev week
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWindowStart(startOfDay(new Date()))}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWindowStart((d) => addDays(d, 7))}>
            Next week <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="In progress"
          value={kpis.inProgress.toLocaleString()}
          subtitle="Operations on the floor"
          icon={<Timer className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Planned (window)"
          value={kpis.plannedThisWindow.toLocaleString()}
          subtitle={`Next ${VISIBLE_DAYS} days`}
          icon={<PackageSearch className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Overdue"
          value={kpis.overdue.toLocaleString()}
          subtitle={kpis.overdue ? 'Action needed' : 'On schedule'}
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.overdue ? 'text-danger' : 'text-success'}`} />}
        />
        <KpiCard
          title="Completed (7d)"
          value={kpis.completedThisWeek.toLocaleString()}
          subtitle="Past 7 days"
          icon={<CheckCircle2 className="h-5 w-5 text-success" />}
        />
      </div>

      <Card className="overflow-hidden p-0">
        {/* Day-axis header */}
        <div className="grid border-b border-border bg-surface-card-hover" style={gridTemplate()}>
          <div className="border-r border-border px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-content-muted">
            Work centre
          </div>
          {days.map((d, i) => {
            const isToday = sameDay(d, today);
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            return (
              <div
                key={i}
                className={`border-r border-border/60 px-2 py-2 text-center text-[11px] ${
                  isToday ? 'bg-primary/10 font-semibold text-primary' : isWeekend ? 'bg-content-muted/5 text-content-muted' : 'text-content-secondary'
                }`}
              >
                <div>{d.toLocaleDateString('en-IN', { weekday: 'short' })}</div>
                <div className="font-medium">{d.getDate()}</div>
              </div>
            );
          })}
        </div>

        {/* Lanes */}
        {loading && lanes.length === 0 ? (
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading schedule…
          </div>
        ) : lanes.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-12 text-sm text-content-muted">
            <Factory size={32} className="opacity-40" />
            <p>No work centres yet. Add at least one under <em>Manufacturing → Work Centers</em>.</p>
          </div>
        ) : (
          lanes.map((lane) => (
            <div
              key={lane.id}
              className="grid border-b border-border/60"
              style={{ ...gridTemplate(), minHeight: ROW_HEIGHT + 16 }}
            >
              <div className="border-r border-border bg-surface-card px-3 py-2">
                <div className="truncate text-sm font-medium text-content-primary">{lane.name}</div>
                <div className="text-[11px] text-content-muted">
                  {lane.ops.length} order(s){lane.capacityPerDay ? ` · ${lane.capacityPerDay}/day` : ''}
                </div>
              </div>
              <div className="relative col-span-14" style={{ gridColumn: `2 / span ${VISIBLE_DAYS}` }}>
                {/* Day grid lines */}
                <div className="pointer-events-none absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${VISIBLE_DAYS}, minmax(0, 1fr))` }}>
                  {days.map((d, i) => {
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <div
                        key={i}
                        className={`border-r border-border/30 ${isWeekend ? 'bg-content-muted/5' : ''}`}
                      />
                    );
                  })}
                </div>
                {/* Today line */}
                {todayOffset >= 0 && todayOffset < VISIBLE_DAYS && (
                  <div
                    className="pointer-events-none absolute top-0 z-10 h-full w-px bg-primary"
                    style={{ left: `${(todayOffset / VISIBLE_DAYS) * 100}%` }}
                  />
                )}
                {/* Op bars */}
                {lane.ops.map((op) => {
                  const placed = placeOp(op, windowStart);
                  if (!placed) return null;
                  const { left, width } = placed;
                  const isSelected = selected?.id === op.id;
                  return (
                    <button
                      key={op.id}
                      type="button"
                      onClick={() => setSelected(op)}
                      className={`absolute top-1.5 z-20 flex h-[28px] items-center overflow-hidden rounded-md border px-2 text-left text-[11px] font-medium shadow-sm transition ${
                        isSelected ? 'ring-2 ring-primary' : ''
                      } ${barClasses(op.status)}`}
                      style={{ left: `${left}%`, width: `${Math.max(width, 4)}%` }}
                      title={`${op.label} · ${op.qty || ''} · ${op.status}`}
                    >
                      <span className="truncate">{op.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </Card>

      {selected && (
        <Card>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-content-muted">Selected operation</div>
              <h3 className="text-base font-semibold text-content-primary">{selected.label}</h3>
              <p className="text-xs text-content-secondary">
                {selected.productName} · Order {selected.orderCode || '—'}
              </p>
            </div>
            <button
              type="button"
              className="text-content-muted hover:text-danger"
              onClick={() => setSelected(null)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            <Stat label="Start" value={fmtDateTime(selected.start)} />
            <Stat label="End" value={fmtDateTime(selected.end)} />
            <Stat label="Quantity" value={selected.qty.toLocaleString()} />
            <Stat label="Status" value={selected.status} cls={statusTextColor(selected.status)} />
          </div>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-content-muted">
        <span>Legend:</span>
        <LegendChip label="Planned"   cls="border-info/40 bg-info/10 text-info" />
        <LegendChip label="In progress" cls="border-warning/40 bg-warning/10 text-warning" />
        <LegendChip label="Completed" cls="border-success/40 bg-success/10 text-success" />
        <LegendChip label="Held"      cls="border-content-muted/40 bg-content-muted/10 text-content-muted" />
        <LegendChip label="Cancelled" cls="border-danger/40 bg-danger/10 text-danger" />
      </div>
    </div>
  );
}

function Stat({ label, value, cls }: { label: string; value: string | number; cls?: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-card px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-content-muted">{label}</div>
      <div className={`text-sm font-semibold ${cls ?? 'text-content-primary'}`}>{value}</div>
    </div>
  );
}

function LegendChip({ label, cls }: { label: string; cls: string }) {
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 ${cls}`}>{label}</span>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function gridTemplate(): React.CSSProperties {
  return {
    gridTemplateColumns: `220px repeat(${VISIBLE_DAYS}, minmax(0, 1fr))`,
  };
}

function placeOp(op: ResolvedOp, windowStart: Date): { left: number; width: number } | null {
  const windowEnd = addDays(windowStart, VISIBLE_DAYS);
  if (op.end < windowStart || op.start >= windowEnd) return null;
  const start = op.start < windowStart ? windowStart : op.start;
  const end = op.end > windowEnd ? windowEnd : op.end;
  const left = ((start.getTime() - windowStart.getTime()) / DAY_MS / VISIBLE_DAYS) * 100;
  const width = (Math.max(end.getTime() - start.getTime(), DAY_MS / 4) / DAY_MS / VISIBLE_DAYS) * 100;
  return { left, width };
}

function barClasses(s: OpStatus): string {
  if (s === 'Completed') return 'border-success/40 bg-success/15 text-success';
  if (s === 'InProgress') return 'border-warning/40 bg-warning/20 text-warning';
  if (s === 'Held') return 'border-content-muted/40 bg-content-muted/15 text-content-muted';
  if (s === 'Cancelled') return 'border-danger/40 bg-danger/15 text-danger line-through';
  return 'border-info/40 bg-info/15 text-info';
}

function statusTextColor(s: OpStatus): string {
  if (s === 'Completed') return 'text-success';
  if (s === 'InProgress') return 'text-warning';
  if (s === 'Held') return 'text-content-muted';
  if (s === 'Cancelled') return 'text-danger';
  return 'text-info';
}

function normaliseStatus(raw: string): OpStatus {
  const s = raw.trim().toLowerCase();
  if (s === 'inprogress' || s === 'in_progress' || s === 'in progress' || s === 'running' || s === 'remaining' || s === 'wip') return 'InProgress';
  if (s === 'completed' || s === 'done' || s === 'finished') return 'Completed';
  if (s === 'held' || s === 'hold' || s === 'paused') return 'Held';
  if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
  return 'Planned';
}

function parseDate(v: unknown): Date | null {
  if (typeof v !== 'string' || !v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function fmtDateTime(d: Date): string {
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}
